import {
  cannectDefaultProduct,
  cannectDefaultProductCapsule,
  cannectDefaultProductCream,
  cannectDefaultProductGummy,
  cannectDefaultProductNoTarg,
  cannectDefaultProductOil,
  aspaecDefaultProduct
} from '@cannect/assets/img'
import { useAuth } from '@cannect/hooks/AuthContext'
import ActionCards from '@cannect/new-components/ActionCards'
import Modal from '@cannect/new-components/Modal'
import { ProductModalDisabled } from '@cannect/new-components/ProductModalDisabled'
import { ProductModalDisabledNutraceutico } from '@cannect/new-components/ProductModalDisabledNutraceutico'
import Steps from '@cannect/new-components/Steps/Steps'
import Testimonials from '@cannect/new-components/Testimonials'
import {
  Accordion,
  Badge,
  Button,
  PrescriptionBadges,
  SoldByPill,
  TabsPrimitive,
  Typography
} from '@cannect/new-components/atoms'
import CuratorshipGauge from '@cannect/new-components/atoms/CuratorshipGauge'
import { CuratorshipInfo } from '@cannect/new-components/atoms/CuratorshipGauge/CuratorshipInfo'
import { TooltipText } from '@cannect/new-components/atoms/Tooltip'
import { Product, QuantityCounter } from '@cannect/new-components/molecules'
import BreadCrumb from 'components/BreadCrumb'
import Loading from 'components/Loading'
import SEO from 'components/SEO'
import { Bell, Info, Package, ShoppingCart } from 'lucide-react'
import { useState } from 'react'
import { useParams } from 'react-router-dom'
import SwiperCore, { Navigation, Pagination } from 'swiper'
import { Swiper, SwiperSlide } from 'swiper/react'
import 'swiper/css'
import 'swiper/css/navigation'
import 'swiper/css/pagination'
import { pickProductCoverImagePath, pickProductGalleryImagePaths } from '@cannect/utils/productMedia'
import { pickAffinityTag } from '@cannect/utils/productAffinityTag'
import { getPillPmcPromo } from '@cannect/utils/pillPmcPromo'
import {
  getDisplayableTiers,
  isAmountTrigger,
  getTierTriggerTotal,
  findActiveTier
} from '@cannect/services/resources/progressive-discounts'
import RelatedProduct from './RelatedProduct'
import { ProgressiveDiscountTiers } from './components/ProgressiveDiscountTiers'
import { useProductDetails } from './useProductDetails'

SwiperCore.use([Navigation, Pagination])

export const ProductDetailsUI = ({
  isLoading,
  productDetails,
  handleAddToCartClick,
  handleAddToCart,
  redirectToProductPrescription,
  loadingToCart,
  handleCloseFlavorModal,
  isPrescriber,
  nutraceuticosModal,
  setNutraceuticosModal,
  anvisaModalMessage,
  modalUrlClickButton,
  learnMore,
  prescriber,
  quantity,
  setQuantity,
  flavorModal,
  handleChangeFlavor,
  tokenApi,
  handleGoToLogin,
  handleNotifyMeDetail,
  isSupplementProduct,
  topicProductWarningModal,
  setTopicProductWarningModal,
  topicProductWarningMessage,
  expiringBatchWarningModal,
  setExpiringBatchWarningModal,
  expiringBatchWarningMessage,
  hasProgressiveDiscount,
  progressiveTiers,
  progressiveRule
}: ReturnType<typeof useProductDetails>) => {
  const { role } = useAuth()
  const { brand, name, id } = useParams<{ brand?: string; name?: string; id?: string }>()
  // Fotos reais do produto e selos de curadoria só aparecem para registration_type === 2.
  const isRegistrationType2 = role === 2
  // Helpers
  const fmtBRL = (n: number | string | null | undefined) =>
    Number(n || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

  const activeBrandPromo = Array.isArray(productDetails?.brand?.promotion)
    ? productDetails.brand.promotion.find((p) => p?.status === 'active')
    : null

  const isTesteGenetico = productDetails?.type === 'TESTE_GENETICO'

  // Preço de tabela (PMC/ABCFARMA) de produtos Pill — riscado + % igual ao padrão de promoção já
  // usado abaixo (hasPromo/discountPercentage/displayPrice), só entra quando não há promoção "real"
  // (valid_promotion/promotion/activeBrandPromo), que Pill nunca tem.
  const pillPmcPromo = getPillPmcPromo(productDetails)

  const regularPrice = Number(
    productDetails?.valid_promotion?.regular_price ??
      productDetails?.promotion?.regular_price ??
      activeBrandPromo?.regular_price ??
      pillPmcPromo?.oldPrice ??
      productDetails?.real_price ??
      productDetails?.price ??
      0
  )

  const promoPrice = Number(
    productDetails?.valid_promotion?.promotional_price ??
      productDetails?.promotion?.promotional_price ??
      activeBrandPromo?.promotional_price ??
      pillPmcPromo?.newPrice ??
      0
  )

  const hasPromo = Number.isFinite(promoPrice) && promoPrice > 0 && promoPrice < regularPrice
  const discountPercentage =
    hasPromo && regularPrice > 0 ? Math.round(((regularPrice - promoPrice) / regularPrice) * 100) : 0

  const isShippingOnlyProgressive = progressiveRule?.benefit_target === 'shipping'
  const maxProgressivePct = !isShippingOnlyProgressive
    ? Math.round(
        getDisplayableTiers(progressiveTiers).reduce(
          (max, tier) => Math.max(max, Number(tier.discount_percent) || 0),
          0
        )
      )
    : 0

  let discountLabel: string | null = null
  if (hasProgressiveDiscount && maxProgressivePct > 0) {
    discountLabel = `Até -${maxProgressivePct}% off`
  } else if (discountPercentage > 0) {
    discountLabel = `Até -${discountPercentage}% off`
  }

  // Progressivo sempre calculado sobre o preço ORIGINAL (nunca sobre o promocional) — mesma regra
  // já aplicada no carrinho. Quando a faixa selecionada (quantity) é atingida, o progressivo vence
  // a promoção no preço principal exibido.
  const activeProgressiveTier = hasProgressiveDiscount
    ? findActiveTier(
        [...getDisplayableTiers(progressiveTiers)].sort((a, b) => a.min_quantity - b.min_quantity),
        getTierTriggerTotal({ quantity, unitPrice: regularPrice, triggerType: progressiveRule?.trigger_type })
      )
    : undefined
  const progressivePct = activeProgressiveTier ? Number(activeProgressiveTier.discount_percent) || 0 : 0
  const isProgressiveActive = progressivePct > 0 && regularPrice > 0
  const progressivePrice = isProgressiveActive ? +(regularPrice * (1 - progressivePct / 100)).toFixed(2) : 0

  let displayPrice = regularPrice
  let displayDiscountPct = 0
  if (isProgressiveActive) {
    displayPrice = progressivePrice
    displayDiscountPct = progressivePct
  } else if (hasPromo) {
    displayPrice = promoPrice
    displayDiscountPct = discountPercentage
  }

  const isSupplements = typeof isSupplementProduct === 'boolean' ? isSupplementProduct : productDetails?.class?.id === 6

  if (isLoading)
    return (
      <div className="h-screen">
        <Loading loading={isLoading} />
      </div>
    )

  const getProductImage = (product: any) => {
    const isNationalProduct = product?.supplier?.id === 18
    const defaultImage = isNationalProduct ? cannectDefaultProduct : cannectDefaultProductNoTarg
    const imgMedia = pickProductCoverImagePath(product.media)
    const shapeName = product?.shape?.name || ''
    const shapeNameFormatted = shapeName?.toLowerCase().replace(' ', '-')
    const isSupplementProduct = product?.class?.id === 6 || product?.class_id === 6
    const deliveryByAssociation = product?.delivery_by_association
    const isPillProduct = product?.origin === 'pill'

    // `role` vem de getUserRoles(), que devolve 0 quando nao ha usuario na sessao.
    // A regra so contemplava paciente logado (role 1), entao o visitante caia no
    // ramo permissivo e via a foto real -- o oposto do pretendido.
    const isGuest = !role
    const useGenericImage = (isGuest || role === 1) && !isSupplementProduct

    if (isPillProduct) {
      return imgMedia || defaultImage
    }

    if (deliveryByAssociation) {
      return aspaecDefaultProduct
    }

    let imageSource = ''

    switch (shapeNameFormatted) {
      case 'óleo':
        imageSource = useGenericImage ? cannectDefaultProductOil : imgMedia || cannectDefaultProductOil
        break
      case 'cápsula':
        imageSource = useGenericImage ? cannectDefaultProductCapsule : imgMedia || cannectDefaultProductCapsule
        break
      case 'creme':
        imageSource = useGenericImage ? cannectDefaultProductCream : imgMedia || cannectDefaultProductCream
        break
      case 'goma':
        imageSource = useGenericImage ? cannectDefaultProductGummy : imgMedia || cannectDefaultProductGummy
        break
      default: {
        // Fallback para IDs numéricos (compatibilidade)
        const shapeId = product?.shape?.id || product?.shape_id
        if (shapeId === 1) {
          imageSource = useGenericImage ? cannectDefaultProductOil : imgMedia || cannectDefaultProductOil
        } else if (shapeId === 2) {
          imageSource = useGenericImage ? cannectDefaultProductCapsule : imgMedia || cannectDefaultProductCapsule
        } else if (shapeId === 4) {
          imageSource = useGenericImage ? cannectDefaultProductCream : imgMedia || cannectDefaultProductCream
        } else if (shapeId === 6) {
          imageSource = useGenericImage ? cannectDefaultProductGummy : imgMedia || cannectDefaultProductGummy
        } else {
          imageSource = useGenericImage ? defaultImage : imgMedia || defaultImage
        }
      }
    }

    return imageSource
  }

  const productName = productDetails?.translated_name || productDetails?.name || 'Produto'
  const productBrand = productDetails?.brand?.name || ''
  const productDescription =
    productDetails?.details || `${productName}. Produto de cannabis medicinal disponível na Cannect.`
  const productImage = pickProductCoverImagePath(productDetails?.media)

  const isPillProduct = productDetails?.origin === 'pill'
  const sellable = (productDetails as any)?.sellable !== false
  const isPatientUserType = role === 1
  // Visitante (role 0) tambem nao ve a foto real: so quem esta logado e nao e
  // paciente, alem de Pill e suplemento.
  const showsRealProductPhoto = isPillProduct || isSupplements || (!!role && !isPatientUserType)
  const galleryImagePaths = pickProductGalleryImagePaths(productDetails?.media)
  const showImageCarousel = showsRealProductPhoto && galleryImagePaths.length > 1

  const relatedProductAffinityTag = pickAffinityTag(productDetails?.shopifyTags)
  const relatedProductFilters = relatedProductAffinityTag
    ? `shopify_tag=${encodeURIComponent(relatedProductAffinityTag)}&class_id=${productDetails?.class?.id}`
    : `class_id=${productDetails?.class?.id}&shape_id=${productDetails?.shape?.id}`

  if (!productDetails?.id) return null
  return (
    <main className="w-full max-w-[1200px] px-4 sm:px-6 lg:px-8">
      <SEO
        title={`${productName}`}
        description={productDescription}
        canonical={`/detalhes-produtos/${brand || ''}/${name || ''}/${id || productDetails?.id}`}
        image={productImage}
        type="product"
        keywords={`${productName}, ${productBrand}, cannabis medicinal, CBD, canabidiol, ${productDetails?.shape?.name || ''}`}
      />
      <div className="flex w-full flex-col items-center justify-between md:mb-0 md:flex-row">
        <BreadCrumb
          paths={[
            { label: 'Medicamentos', link: '/produtos/1' },
            { label: productDetails?.class?.name || 'Cannabis medicinal', link: '/produtos/1' },
            {
              label: productDetails?.shape?.name === 'Óleo' ? 'Óleos' : productDetails?.shape?.name || '—',
              link: '/produtos/1'
            },
            { label: `${productDetails?.brand?.name} - ${productDetails?.name}`, link: '#' }
          ]}
        />
      </div>

      <Product.Root className="grid w-full grid-cols-1 gap-[24px] sm:gap-[24px] lg:grid-cols-2 lg:gap-[24px]">
        <div className="flex h-full max-h-[500px] w-full flex-col gap-2">
          <div className="relative w-full overflow-hidden rounded-2xl">
            {showImageCarousel ? (
              <Swiper
                navigation
                pagination={{ clickable: true }}
                className="product-details-gallery h-auto max-h-[400px] w-full">
                {galleryImagePaths.map((path) => (
                  <SwiperSlide key={path}>
                    <img
                      src={path}
                      alt={productDetails?.translated_name || productDetails?.name || 'Imagem do produto'}
                      className="h-auto max-h-[400px] w-full rounded-2xl object-contain shadow-none"
                    />
                  </SwiperSlide>
                ))}
              </Swiper>
            ) : (
              <Product.HeaderImageSelo
                src={getProductImage(productDetails)}
                alt={productDetails?.translated_name || productDetails?.name || 'Imagem do produto padrão'}
                isNationalProduct={productDetails?.supplier?.id === 18}
                isSupplementProduct={isSupplements}
                className="h-auto max-h-[400px] w-full rounded-2xl object-contain shadow-none"
                product={productDetails}
                shapeName={productDetails?.shape?.name}
                deliveryByAssociation={(productDetails as any)?.delivery_by_association}
              />
            )}

            {discountLabel && sellable && (tokenApi || isSupplementProduct || isPillProduct) && (
              <div className="pointer-events-none absolute right-8 top-8 z-10 rounded-md bg-warning-100 px-3 py-0.5 text-base italic text-neutral-600 shadow-sm">
                {discountLabel}
              </div>
            )}
          </div>

          <Product.CurationBadges product={productDetails} isPrescriber={isRegistrationType2} />
        </div>

        {/* RIGHT: Content */}
        <div
          className={`flex h-full flex-col items-start justify-between gap-4 rounded-2xl border border-neutral-200 bg-white p-4 sm:gap-4 sm:p-6 ${
            hasProgressiveDiscount ? '' : 'max-h-[500px]'
          }`}>
          {/* Brand pill above title */}
          {productDetails?.brand?.name && (
            <div className="inline-flex items-center gap-2 rounded-md bg-secondary-100 px-3 py-1 text-xs text-neutral-500">
              {productDetails.brand.name}
            </div>
          )}

          {/* Title */}
          <Typography.Heading
            type="headingFour"
            weight="semibold"
            className="text-balance text-lg leading-tight sm:text-xl md:text-2xl">
            {productDetails?.translated_name || productDetails?.name}
          </Typography.Heading>

          {/* Curadoria (percentual de aderência) */}
          <div className="flex items-start gap-2 py-1">
            <CuratorshipGauge
              percentage={productDetails?.curatorship_original?.percentage}
              variant="full"
              size="sm"
            />
            <CuratorshipInfo className="mt-8" />
          </div>

          {isPillProduct && <SoldByPill />}

          <PrescriptionBadges codes={productDetails?.prescription_badges} className="mt-1" showDetailsOnlyBadges />

          {/* Featured Badge */}
          {(productDetails?.featured === 1 ||
            productDetails?.featured === '1' ||
            Number(productDetails?.featured) === 1) && (
            <div className="mb-3 mt-2">
              <Badge
                variant="tertiary"
                className="inline-flex items-center gap-2 bg-[#E8EEF2] font-medium text-[#5A6C7D]"
                rounded="lg"
                size="xs">
                Destaque
                <TooltipText content="Este produto está em destaque porque reúne alta qualidade, disponibilidade consistente em estoque e uma parceria estratégica com a Cannect, o que garante segurança e continuidade no tratamento dos pacientes.">
                  <Info
                    className="h-3.5 w-3.5 cursor-pointer hover:text-[#4A5C6D]"
                    style={{ verticalAlign: 'middle' }}
                  />
                </TooltipText>
              </Badge>
            </div>
          )}

          {/* Brand + SKU row */}
          {/**
        <div className="mb-1 flex items-center gap-2 text-neutral-500">
          {productDetails?.brand?.name && (
            <Typography.Text type="paragraphTwo" muted weight="semibold">
              {productDetails.brand.name}
            </Typography.Text>
          )}
          <span className="h-1.5 w-1.5 rounded-full bg-neutral-300" />
          <Typography.Text type="paragraphTwo" muted>
            SKU: {productDetails?.id}
          </Typography.Text>
        </div>
        */}

          {/* Logistics badges (national, import) */}
          {productDetails?.supplier?.id === 18 && (
            <Badge variant="neutral_light" rounded="md" className="text-primary-700">
              Entrega de origem nacional, receba em até <span className="font-bold">72 horas</span> após a confirmação
              de pagamento.
            </Badge>
          )}

          {/* Associação obrigatória (ASPAEC) */}
          {(productDetails as any)?.delivery_by_association && (
            <div className="flex items-center gap-2">
              <Badge variant="warning" rounded="md" className="text-neutral-600" size="xs">
                Associação obrigatória
              </Badge>
              <TooltipText content="Este produto é destinado aos associados da ASPAEC. Caso deseje mais informações sobre como se tornar um associado, entre em contato com o nosso time pelo número (11) 98271-1992.">
                <Info className="h-4 w-4 text-neutral-500" />
              </TooltipText>
            </div>
          )}

          {productDetails?.class?.id === 6 && productDetails?.brand?.id === 108 && (
            <Badge variant="success" rounded="md">
              Custo de importação Grátis
            </Badge>
          )}

          {/* Price block – left old price + promo %, right big current price.
              Inapto a venda nao mostra preco para ninguem, nem logado: o CTA vira
              "Avise-me" e preco de algo que nao pode ser comprado confunde. */}
          {sellable && (tokenApi || isSupplementProduct || isPillProduct || !(productDetails as any)?.is_cannabis) && (
            <div className="flex items-baseline gap-3">
              {(isProgressiveActive || hasPromo) && (
                <>
                  <Typography.Text type="captionOne" className="text-sm italic text-neutral-500 line-through">
                    R$ {fmtBRL(regularPrice)}
                  </Typography.Text>
                  <span className="text-sm italic text-neutral-500">-{displayDiscountPct}%</span>
                </>
              )}

              <Product.Price
                currentPrice={displayPrice}
                className="ml-1"
                textClassName="font-semibold text-2xl leading-[140%] tracking-normal"
              />
            </div>
          )}

          {!!productDetails?.flavors?.some((item) => item?.status === 'A') && (
            <div className="w-full">
              <Typography.Text weight="semibold" className="mb-2">
                Sabor:
              </Typography.Text>

              <div className="flex flex-wrap gap-2 sm:gap-3">
                {productDetails?.flavors?.map((item) => {
                  const isRecommended = !!item?.isRecommendedFlavor
                  const isActive = item.flavor === productDetails?.flavor
                  const isLowStock = item?.balance && item?.balance <= 0

                  const getFlavorTextColor = () => {
                    if (isRecommended) return 'text-warning-600'
                    if (isLowStock) return 'text-danger-500'
                    return 'text-transparent'
                  }

                  const getFlavorText = () => {
                    if (isRecommended) return 'Recomendado'
                    if (isLowStock) return 'Possível tempo maior de entrega'
                    return '.'
                  }

                  return (
                    item?.status === 'A' && (
                      <div className="flex flex-col justify-end gap-1.5" key={item.id}>
                        <Typography.Text
                          type="captionOne"
                          className={`w-full text-center text-xs sm:text-sm ${getFlavorTextColor()}`}>
                          {getFlavorText()}
                        </Typography.Text>

                        <Badge
                          variant={isActive ? 'primary_medium' : 'outline_medium'}
                          rounded="full"
                          className={`inline-flex h-7 w-fit cursor-pointer items-center justify-center rounded-full border-neutral-100 px-3 py-1 text-xs text-neutral-300 sm:h-6 sm:px-2 sm:py-[2px] sm:text-sm ${
                            isActive ? 'bg-primary-600 !text-white' : 'hover:bg-primary-300 hover:!text-white'
                          } ${isRecommended && !isActive ? 'hover:opacity-80' : ''}`}
                          onClick={() => handleChangeFlavor?.(item?.id)}>
                          {item.flavor}
                        </Badge>
                      </div>
                    )
                  )
                })}
              </div>
            </div>
          )}

          {/* Dosagem e volume (render only if provided) */}
          {Array.isArray((productDetails as any)?.dosageVolumes) &&
            (productDetails as any).dosageVolumes.length > 0 && (
              <div className="mt-2 w-full">
                <Typography.Text weight="semibold" className="mb-3">
                  Dosagem e volume
                </Typography.Text>
                <div className="flex flex-wrap gap-3">
                  {(productDetails as any).dosageVolumes.map((opt: any) => (
                    <Badge
                      key={opt.id ?? opt.value}
                      variant={opt.active ? 'primary_medium' : 'outline_medium'}
                      rounded="full"
                      className="cursor-pointer px-4"
                      onClick={opt.onClick}>
                      {opt.label}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

          {/* Faixas de desconto progressivo (FR-001) — acima do botão de adicionar ao carrinho */}
          {(tokenApi || isSupplementProduct) && hasProgressiveDiscount && progressiveTiers && (
            <ProgressiveDiscountTiers
              tiers={progressiveTiers}
              triggerType={progressiveRule?.trigger_type}
              conditionScope={progressiveRule?.condition_scope}
              onSelectTier={isAmountTrigger(progressiveRule?.trigger_type) ? undefined : setQuantity}
              productPrice={regularPrice || undefined}
            />
          )}

          {/* CTA row */}
          <div className="mt-auto flex w-full flex-row flex-wrap items-center gap-3">
            {sellable === false ? (
              <Button
                full
                size="lg"
                variant="primary_medium"
                iconPlacement="left"
                icon={<Bell />}
                onClick={handleNotifyMeDetail}
                className="min-w-auto h-12 whitespace-normal break-words rounded-xl px-3 text-center sm:h-10">
                Avise-me quando disponível
              </Button>
            ) : (
              <>
                {(tokenApi || !isSupplementProduct) && (
                  <>
                    <QuantityCounter quantity={quantity} onChangeQuantity={setQuantity} isLoading={loadingToCart} />
                    <Button
                      size="lg"
                      variant="primary_medium"
                      iconPlacement="left"
                      icon={<ShoppingCart />}
                      isLoading={loadingToCart}
                      onClick={() => handleAddToCartClick?.(productDetails as any, quantity)}
                      className="h-12 min-w-0 flex-1 whitespace-normal break-words rounded-xl px-3 text-center sm:h-10">
                      Adicionar ao carrinho
                    </Button>
                  </>
                )}

                {!tokenApi && isSupplementProduct && (
                  <div className="flex w-full flex-col gap-3 sm:flex-row sm:gap-2">
                    <Button
                      full
                      size="xl"
                      variant="outline_medium"
                      onClick={() => window.open('https://atendimento.cannect.life/atendimento-cannect', '_blank')}
                      className="h-12 rounded-2xl sm:flex-1">
                      QUERO CONSULTAR UM MÉDICO
                    </Button>
                    <Button
                      size="xl"
                      full
                      variant="primary_medium"
                      onClick={() => {
                        if (typeof window !== 'undefined') {
                          window?.location?.assign?.('/login')
                        }
                      }}
                      className="h-12 rounded-2xl sm:flex-1">
                      fazer login para ver mais
                    </Button>
                  </div>
                )}
              </>
            )}

            {isPrescriber && tokenApi && (
              <Button
                size="lg"
                variant="outline_medium"
                onClick={redirectToProductPrescription}
                className="h-12 w-full max-w-full rounded-xl px-4 py-2 text-center text-sm sm:h-10 sm:min-w-0 sm:flex-1">
                Prescrever
              </Button>
            )}
          </div>

          {/* Disclaimer */}
          {/* <div className="mt-4 w-full pt-3 text-neutral-600">
            <Typography.Text weight="semibold" className="mb-1 text-sm">
              Disclaimer
            </Typography.Text>
            <Typography.Text type="paragraphTwo" className="mt-3 text-xs sm:text-sm leading-relaxed">
              Lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et
              dolore magna aliqua. Ut enim ad minim veniam, quis nostrud exercitation ullamco laboris nisi ut aliquip ex
              ea commodo consequat.
            </Typography.Text>
          </div> */}
        </div>
      </Product.Root>

      {/* Combo: formulações incluídas */}
      {(() => {
        const comboItems = ((productDetails as any)?.combo_products || []).filter((cp: any) => !cp.product?.type || cp.product.type === 'PRODUTO')
        const isCombo = (productDetails as any)?.is_combo && comboItems.length > 0
        if (!isCombo) return null
        return (
          <div className="mx-auto mt-7 w-full max-w-[1200px]">
            <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
              <div className="flex items-center gap-2.5 border-b border-neutral-100 bg-neutral-50 px-5 py-3">
                <Package size={17} className="text-neutral-500" />
                <div>
                  <span className="text-[13px] font-semibold text-neutral-800">
                    Combo &middot; {comboItems.length} formulações
                  </span>
                  <p className="text-[11px] text-neutral-500">
                    Produtos incluídos neste combo
                  </p>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3 p-5 sm:grid-cols-2">
                {comboItems.map((cp: any, idx: number) => (
                  <div
                    key={cp.id}
                    className="flex items-center gap-3.5 rounded-xl border border-neutral-100 bg-neutral-50/50 p-3 transition-colors hover:border-neutral-200 hover:bg-neutral-50">
                    <div className="relative shrink-0">
                      <img
                        src={cp.product?.media?.[0]?.path || getProductImage(productDetails)}
                        alt={cp.product?.translated_name || cp.product?.name}
                        className="h-16 w-16 rounded-xl border border-neutral-100 bg-white object-contain p-1.5"
                      />
                      <span className="absolute -right-1 -top-1 flex h-[18px] w-[18px] items-center justify-center rounded-full bg-neutral-700 text-[9px] font-bold text-white">
                        {idx + 1}
                      </span>
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="text-[13px] font-semibold leading-snug text-neutral-800">
                        {cp.product?.translated_name || cp.product?.name}
                      </p>
                      <div className="mt-1 flex items-center gap-1.5">
                        <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600">
                          {cp.quantity || 1} {(cp.quantity || 1) === 1 ? 'unidade' : 'unidades'}
                        </span>
                        {cp.flavor?.name && (
                          <span className="rounded bg-neutral-100 px-1.5 py-0.5 text-[11px] font-medium text-neutral-600">
                            {cp.flavor.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )
      })()}

      <ProductModalDisabledNutraceutico open={nutraceuticosModal} handleCancel={() => setNutraceuticosModal(false)} />

      <ProductModalDisabled
        open={flavorModal}
        handleCancel={handleCloseFlavorModal}
        onClick={async () => {
          if (prescriber) {
            redirectToProductPrescription()
          } else {
            handleAddToCart(productDetails?.id, quantity)
          }
        }}
        learnMore={learnMore}
        urlRedirect={modalUrlClickButton}
        message={anvisaModalMessage}
      />

      <ProductModalDisabled
        open={topicProductWarningModal.open}
        handleCancel={() => setTopicProductWarningModal({ open: false })}
        message={topicProductWarningMessage ?? ''}
        primaryButtonOnly
        primaryButtonLabel="Continuar"
        onClick={async () => {
          if (topicProductWarningModal.product && topicProductWarningModal.quantity != null) {
            const { product, quantity: qty } = topicProductWarningModal
            handleAddToCart(
              product.id,
              qty,
              product.class?.id,
              product.brand?.id ?? product.brand_id ?? undefined,
              product?.type ?? undefined
            )
          }
          setTopicProductWarningModal({ open: false })
        }}
      />

      <ProductModalDisabled
        open={expiringBatchWarningModal.open}
        handleCancel={() => setExpiringBatchWarningModal({ open: false })}
        message={expiringBatchWarningMessage ?? ''}
        primaryButtonOnly
        primaryButtonLabel="Continuar"
        onClick={async () => {
          if (expiringBatchWarningModal.product && expiringBatchWarningModal.quantity != null) {
            const { product, quantity: qty } = expiringBatchWarningModal
            handleAddToCart(
              product.id,
              qty,
              product.class?.id,
              product.brand?.id ?? product.brand_id ?? undefined,
              product?.type ?? undefined
            )
          }
          setExpiringBatchWarningModal({ open: false })
        }}
      />

      <div className="mt-8 sm:mt-0">
        <div className={`w-full bg-white ${isTesteGenetico ? 'min-h-[50px]' : 'min-h-[650px]'}`}>
          <TabsPrimitive.Root defaultValue="general_informations">
            <TabsPrimitive.List className="flex w-full justify-start gap-0 border-0 bg-neutral-50 p-0">
              <TabsPrimitive.Trigger
                className="whitespace-nowrap px-4 py-3 text-sm text-neutral-500 data-[state=active]:border-b-transparent data-[state=active]:bg-white data-[state=active]:text-primary-700 data-[state=active]:shadow-none sm:px-6 sm:text-base"
                value="general_informations">
                Ficha geral
              </TabsPrimitive.Trigger>
              {!!tokenApi && !isTesteGenetico && !!productDetails?.curatorship?.Variabilidade?.['Variabilidade entre CoA e embalagem'] && (
                <TabsPrimitive.Trigger
                  className="whitespace-nowrap rounded-b-none rounded-t-2xl px-4 py-3 text-sm text-neutral-500 data-[state=active]:bg-white data-[state=active]:text-primary-700 sm:px-6 sm:text-base"
                  value="technical_informations">
                  Ficha técnica
                </TabsPrimitive.Trigger>
              )}
              {!isTesteGenetico && productDetails?.showDocumentTab && (
                <TabsPrimitive.Trigger
                  className="whitespace-nowrap rounded-b-none rounded-t-2xl px-4 py-3 text-sm text-neutral-500 data-[state=active]:bg-white data-[state=active]:text-primary-700 sm:px-6 sm:text-base"
                  value="coa_informations">
                  {productDetails?.documentTabLabel ?? 'COA'}
                </TabsPrimitive.Trigger>
              )}
            </TabsPrimitive.List>

            <div className="flex w-full items-start justify-start rounded-2xl rounded-t-none p-4 sm:p-6 md:p-8">
              <TabsPrimitive.Content
                value="general_informations"
                className="w-full space-y-4 rounded-2xl bg-neutral-50 px-4 py-8">
                <div className="rounded bg-white px-3 py-2 sm:px-4 md:px-8">
                  <Typography.Text className="whitespace-pre-line text-sm sm:text-base">
                    {productDetails?.details}
                  </Typography.Text>
                </div>

                {!isTesteGenetico &&
                  productDetails?.general_informations &&
                  productDetails?.general_informations
                    .filter((item) => !isPillProduct || !['Extração e diluição', 'THC', 'CBD'].includes(item?.title || ''))
                    .map((item) =>
                    item?.children?.length ? (
                      <Accordion.Root type="single" collapsible key={`${item.title}-${item.description}`}>
                        <Accordion.Item value={item.title}>
                          <Accordion.Trigger className="grid grid-cols-[100px_1fr_auto] gap-2 rounded rounded-b-none bg-white px-3 py-2 hover:bg-neutral-100 hover:no-underline sm:grid-cols-[120px_1fr_auto] sm:px-4 md:grid-cols-[300px_1fr_auto] md:px-8">
                            <Typography.Text
                              type="captionOne"
                              weight="semibold"
                              className="text-start text-xs sm:text-sm md:text-base">
                              {item.title}
                            </Typography.Text>
                            <div className="flex text-start">
                              <Typography.Text
                                type="captionOne"
                                className="whitespace-pre-line text-start text-xs sm:text-sm md:text-base">
                                {item.description}
                              </Typography.Text>
                            </div>
                          </Accordion.Trigger>
                          {!isTesteGenetico && (
                            <Accordion.Content>
                              <div className="mt-2 space-y-2 px-2 sm:px-3 md:px-4">
                                {item.children.map((child) => (
                                  <div
                                    key={`${child.title}-${child.description}`}
                                    className="grid grid-cols-[100px_1fr] items-center gap-2 rounded bg-white px-4 py-2 sm:grid-cols-[120px_1fr] sm:px-6 md:grid-cols-[300px_1fr] md:px-8">
                                    <Typography.Text
                                      type="captionOne"
                                      className="text-xs sm:text-sm md:text-base"
                                      weight="semibold">
                                      {child.title}
                                    </Typography.Text>
                                    <Typography.Text
                                      type="captionOne"
                                      className="whitespace-pre-line text-xs sm:text-sm md:text-base">
                                      {child.description}
                                    </Typography.Text>
                                  </div>
                                ))}
                              </div>
                            </Accordion.Content>
                          )}
                        </Accordion.Item>
                      </Accordion.Root>
                    ) : (
                      !isTesteGenetico && (
                        <div
                          key={`${item.title}-${item.description}`}
                          className="grid grid-cols-[100px_1fr] items-center gap-2 rounded bg-white px-3 py-2 sm:grid-cols-[120px_1fr] sm:px-4 md:grid-cols-[300px_1fr] md:px-8">
                          <Typography.Text
                            type="captionOne"
                            className="text-xs sm:text-sm md:text-base"
                            weight="semibold">
                            {item.title}
                          </Typography.Text>
                          <Typography.Text type="captionOne" className="text-xs sm:text-sm md:text-base">
                            {item.description}
                          </Typography.Text>
                        </div>
                      )
                    )
                  )}
              </TabsPrimitive.Content>
              {!isTesteGenetico &&
                productDetails?.curatorship?.Variabilidade?.['Variabilidade entre CoA e embalagem'] && (
                  <TabsPrimitive.Content
                    value="technical_informations"
                    className="w-full space-y-4 rounded-2xl bg-neutral-50 px-4 py-8">
                    {/* Informações de Canabinoides */}
                    {(productDetails?.medical_data?.cbd ||
                      productDetails?.medical_data?.thc ||
                      productDetails?.medical_data?.dropsPerMilliliter) && (
                      <Accordion.Root type="single" collapsible>
                        <Accordion.Item value="canabinoides">
                          <Accordion.Trigger className="grid grid-cols-[120px_1fr_auto] gap-2 rounded rounded-b-none bg-white px-3 py-2 hover:bg-neutral-100 hover:no-underline md:grid-cols-[300px_1fr_auto] md:px-8">
                            <Typography.Text type="captionOne" weight="semibold" className="text-start md:text-base">
                              Canabinoides
                            </Typography.Text>
                            <div className="flex text-start" />
                          </Accordion.Trigger>
                          <Accordion.Content>
                            <div className="mt-2 space-y-2 px-2 md:px-4">
                              {/* Gotas/mL */}
                              {productDetails?.medical_data?.dropsPerMilliliter && (
                                <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                  <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                    Gotas/mL
                                  </Typography.Text>
                                  <Typography.Text type="captionOne" className="md:text-base">
                                    {Math.round(Number(productDetails.medical_data.dropsPerMilliliter))}
                                  </Typography.Text>
                                </div>
                              )}

                              {/* CBD */}
                              {productDetails?.medical_data?.cbd && (
                                <>
                                  <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                    <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                      CBD total
                                    </Typography.Text>
                                    <Typography.Text type="captionOne" className="md:text-base">
                                      {productDetails.medical_data.cbd} mg
                                    </Typography.Text>
                                  </div>
                                  {productDetails?.medical_data?.cbd_concentration && (
                                    <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                      <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                        CBD concentração
                                      </Typography.Text>
                                      <Typography.Text type="captionOne" className="md:text-base">
                                        {productDetails.medical_data.cbd_concentration} mg/
                                        {productDetails?.medical_data?.unit_of_measurement || 'mL'}
                                      </Typography.Text>
                                    </div>
                                  )}
                                  {/* Mg por gota - CBD */}
                                  {productDetails?.medical_data?.cbd_concentration &&
                                    productDetails?.medical_data?.dropsPerMilliliter && (
                                      <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                        <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                          CBD mg/gota
                                        </Typography.Text>
                                        <Typography.Text type="captionOne" className="md:text-base">
                                          {(
                                            Number(productDetails.medical_data.cbd_concentration) /
                                            Number(productDetails.medical_data.dropsPerMilliliter)
                                          ).toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                          })}
                                        </Typography.Text>
                                      </div>
                                    )}
                                  {/* Custo por mg - CBD */}
                                  {productDetails?.medical_data?.cbd && (hasPromo ? promoPrice : regularPrice) > 0 && (
                                    <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                      <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                        Custo por mg (CBD)
                                      </Typography.Text>
                                      <Typography.Text type="captionOne" className="md:text-base">
                                        R${' '}
                                        {(
                                          (hasPromo ? promoPrice : regularPrice) /
                                          Number(productDetails.medical_data.cbd)
                                        ).toLocaleString('pt-BR', {
                                          minimumFractionDigits: 4,
                                          maximumFractionDigits: 4
                                        })}
                                      </Typography.Text>
                                    </div>
                                  )}
                                </>
                              )}

                              {/* THC */}
                              {productDetails?.medical_data?.thc && (
                                <>
                                  <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                    <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                      THC total
                                    </Typography.Text>
                                    <Typography.Text type="captionOne" className="md:text-base">
                                      {productDetails.medical_data.thc} mg
                                    </Typography.Text>
                                  </div>
                                  {productDetails?.medical_data?.thc_concentration && (
                                    <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                      <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                        THC concentração
                                      </Typography.Text>
                                      <Typography.Text type="captionOne" className="md:text-base">
                                        {productDetails.medical_data.thc_concentration} mg/
                                        {productDetails?.medical_data?.unit_of_measurement || 'mL'}
                                      </Typography.Text>
                                    </div>
                                  )}
                                  {/* Mg por gota - THC */}
                                  {productDetails?.medical_data?.thc_concentration &&
                                    productDetails?.medical_data?.dropsPerMilliliter && (
                                      <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                        <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                          THC mg/gota
                                        </Typography.Text>
                                        <Typography.Text type="captionOne" className="md:text-base">
                                          {(
                                            Number(productDetails.medical_data.thc_concentration) /
                                            Number(productDetails.medical_data.dropsPerMilliliter)
                                          ).toLocaleString('pt-BR', {
                                            minimumFractionDigits: 2,
                                            maximumFractionDigits: 2
                                          })}
                                        </Typography.Text>
                                      </div>
                                    )}
                                  {/* Custo por mg - THC */}
                                  {productDetails?.medical_data?.thc && (hasPromo ? promoPrice : regularPrice) > 0 && (
                                    <div className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                      <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                        Custo por mg (THC)
                                      </Typography.Text>
                                      <Typography.Text type="captionOne" className="md:text-base">
                                        R${' '}
                                        {(
                                          (hasPromo ? promoPrice : regularPrice) /
                                          Number(productDetails.medical_data.thc)
                                        ).toLocaleString('pt-BR', {
                                          minimumFractionDigits: 4,
                                          maximumFractionDigits: 4
                                        })}
                                      </Typography.Text>
                                    </div>
                                  )}
                                </>
                              )}
                            </div>
                          </Accordion.Content>
                        </Accordion.Item>
                      </Accordion.Root>
                    )}

                    {/* Terpenos */}
                    {(productDetails as any)?.curatorship_original?.terpenes_observation && (
                      <Accordion.Root type="single" collapsible>
                        <Accordion.Item value="terpenos">
                          <Accordion.Trigger className="grid grid-cols-[120px_1fr_auto] gap-2 rounded rounded-b-none bg-white px-3 py-2 hover:bg-neutral-100 hover:no-underline md:grid-cols-[300px_1fr_auto] md:px-8">
                            <Typography.Text type="captionOne" weight="semibold" className="text-start md:text-base">
                              Terpenos
                            </Typography.Text>
                            <div className="flex text-start" />
                          </Accordion.Trigger>
                          <Accordion.Content>
                            <div className="mt-2 space-y-2 px-2 md:px-4">
                              <div className="rounded bg-white px-8 py-2">
                                <Typography.Text type="captionOne" className="whitespace-pre-line md:text-base">
                                  {(productDetails as any).curatorship_original?.terpenes_observation || 'N/D'}
                                </Typography.Text>
                              </div>
                            </div>
                          </Accordion.Content>
                        </Accordion.Item>
                      </Accordion.Root>
                    )}

                    {/* Outros Componentes */}
                    {(productDetails as any)?.curatorship_original?.other_components_observation && (
                      <Accordion.Root type="single" collapsible>
                        <Accordion.Item value="outros_componentes">
                          <Accordion.Trigger className="grid grid-cols-[120px_1fr_auto] gap-2 rounded rounded-b-none bg-white px-3 py-2 hover:bg-neutral-100 hover:no-underline md:grid-cols-[300px_1fr_auto] md:px-8">
                            <Typography.Text type="captionOne" weight="semibold" className="text-start md:text-base">
                              Outros componentes
                            </Typography.Text>
                            <div className="flex text-start" />
                          </Accordion.Trigger>
                          <Accordion.Content>
                            <div className="mt-2 space-y-2 px-2 md:px-4">
                              <div className="rounded bg-white px-8 py-2">
                                <Typography.Text type="captionOne" className="whitespace-pre-line md:text-base">
                                  {(productDetails as any).curatorship_original?.other_components_observation || 'N/D'}
                                </Typography.Text>
                              </div>
                            </div>
                          </Accordion.Content>
                        </Accordion.Item>
                      </Accordion.Root>
                    )}

                    {/* Dados de Curadoria */}
                    {Object.entries(productDetails?.curatorship || {}).map(
                      ([category, items]: [string, Record<string, any>]) => {
                        // Pular se for um campo simples (não um objeto)
                        if (typeof items !== 'object' || items === null || Array.isArray(items)) return null
                        // Pular campos que já foram tratados separadamente
                        if (category === 'terpenes_observation' || category === 'other_components_observation')
                          return null

                        return (
                          <Accordion.Root type="single" collapsible key={`${category}`}>
                            <Accordion.Item value={category}>
                              <Accordion.Trigger className="grid grid-cols-[120px_1fr_auto] gap-2 rounded rounded-b-none bg-white px-3 py-2 hover:bg-neutral-100 hover:no-underline md:grid-cols-[300px_1fr_auto] md:px-8">
                                <Typography.Text
                                  type="captionOne"
                                  weight="semibold"
                                  className="text-start md:text-base">
                                  {category}
                                </Typography.Text>
                                <div className="flex text-start" />
                              </Accordion.Trigger>
                              <Accordion.Content>
                                <div className="mt-2 space-y-2 px-2 md:px-4">
                                  {Object.entries(items).map(([title, description]) => (
                                    <div
                                      key={`${title}-${description}`}
                                      className="grid grid-cols-[120px_1fr] items-center gap-2 rounded bg-white px-8 py-2 md:grid-cols-[300px_1fr]">
                                      <Typography.Text type="captionOne" className="md:text-base" weight="semibold">
                                        {title}
                                      </Typography.Text>
                                      <Typography.Text type="captionOne" className="md:text-base">
                                        {(() => {
                                          if (typeof description === 'string') return description
                                          if (description === true) return 'Sim'
                                          if (description === false) return 'Não'
                                          return '-'
                                        })()}
                                      </Typography.Text>
                                    </div>
                                  ))}
                                </div>
                              </Accordion.Content>
                            </Accordion.Item>
                          </Accordion.Root>
                        )
                      }
                    )}

                    {/* Aviso sobre origem das informações */}
                    <div className="mt-4 rounded bg-white px-4 py-3 md:px-8">
                      <Typography.Text type="captionOne" className="text-xs italic text-neutral-600 md:text-sm">
                        As informações preenchidas nesta página foram extraídas e baseadas no COA (Certificate of
                        Analysis) fornecido pelo fabricante.
                      </Typography.Text>
                    </div>
                  </TabsPrimitive.Content>
                )}
              {productDetails?.showDocumentTab && (
                <TabsPrimitive.Content value="coa_informations" className="w-full">
                  <iframe
                    src={productDetails?.coa_url}
                    title={productDetails?.documentTabLabel ?? 'COA'}
                    className="min-h-[650px] w-full min-w-full"
                  />
                </TabsPrimitive.Content>
              )}
            </div>
          </TabsPrimitive.Root>
        </div>
      </div>


      {!isPillProduct && (
        <section className="flex w-full flex-col items-start gap-8 px-4 py-12 md:px-0">
          <Typography.Heading type="headingFour" weight="regular" className="text-2xl font-normal leading-snug">
            Da consulta à entrega: conte com a Cannect em cada etapa
          </Typography.Heading>
          <div className="w-full overflow-x-auto pb-4 lg:overflow-visible lg:pb-0">
            <Steps />
          </div>
      </section>
    )}

      <Testimonials showPromoCard={false} enableHorizontalScroll />

      <RelatedProduct filters={relatedProductFilters} />

      <ActionCards />
    </main>
  )
}
